import { useState } from "react";
import { Link } from "react-router-dom";
import { Carte } from "@/composants/ui/carte";
import { Champ } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { PiedDeListe } from "@/composants/ui/pied-de-liste";
import { useListePaginee } from "@/lib/chargement";
import { dateCourte, nomPersonne } from "@/lib/libelles";
import { api, type AffaireAbregee } from "@/lib/api";

/**
 * Les affaires — le dossier tel que LAB le nomme (ADR-0028).
 *
 * Chaque ligne dit qui suit les commandes qu'on en voit : une affaire peut en
 * regrouper de plusieurs chargés d'affaires (ADR-0032). Restreinte au périmètre
 * comme les autres listes : une affaire n'est visible que si l'une de ses commandes
 * l'est, et la règle vit dans la requête SQL.
 */
export function PageAffaires() {
  const [terme, setTerme] = useState("");
  const [cherche, setCherche] = useState("");
  const liste = useListePaginee<AffaireAbregee>(
    (decalage, signal) => api.affaires({ q: cherche || undefined, decalage }, signal),
    [cherche],
  );

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Affaires</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Une affaire regroupe les commandes d'un même client et d'un même chantier. Elle naît avec la
          première commande ; elle ne se crée pas seule.
        </p>
      </div>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          setCherche(terme.trim());
        }}
      >
        <Champ
          libelle="Filtrer"
          className="min-w-[14rem] flex-1"
          placeholder="Numéro d'affaire, AF-2026-…"
          value={terme}
          onChange={(evenement) => setTerme(evenement.target.value)}
        />
      </form>

      {liste.erreur ? <Erreur message={liste.erreur} onReessayer={liste.recharger} /> : null}
      {!liste.erreur && liste.enCours ? <Chargement message="Chargement des affaires…" /> : null}

      {!liste.enCours && liste.total === 0 ? (
        <Carte>
          <Vide
            titre="Aucune affaire"
            detail={
              cherche
                ? "Aucune affaire de votre périmètre ne porte ce numéro."
                : "Les affaires apparaissent ici avec leur première commande."
            }
          />
        </Carte>
      ) : null}

      {liste.elements.length > 0 ? (
        <Carte>
          <ul className="divide-y divide-filet">
            {liste.elements.map(({ affaire, client, nombre_commandes, proprietaires }) => (
              <li key={affaire.id}>
                <Link
                  to={`/affaires/${affaire.id}`}
                  className="flex flex-col gap-1 px-4 py-3 hover:bg-chene-clair/25 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="font-mono text-sm text-encre sm:w-32 sm:shrink-0">
                    {affaire.numero}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-encre">
                    {client.designation}
                    {client.ville ? (
                      <span className="font-normal text-encre-douce"> — {client.ville}</span>
                    ) : null}
                  </span>
                  <span className="text-sm text-encre-douce sm:w-28 sm:shrink-0">
                    {nombre_commandes} commande{nombre_commandes > 1 ? "s" : ""}
                  </span>
                  {/* Les propriétaires des commandes qu'on voit : l'affaire, elle,
                      ne porte personne (ADR-0032). */}
                  <span className="text-sm text-encre-douce sm:w-44 sm:shrink-0">
                    {proprietaires.map(nomPersonne).join(", ")}
                  </span>
                  <span className="text-sm text-encre-douce sm:w-24 sm:shrink-0">
                    {dateCourte(affaire.cree_le.slice(0, 10))}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <PiedDeListe
            nom="affaire"
            affiches={liste.elements.length}
            total={liste.total}
            reste={liste.reste}
            enCours={liste.chargeLaSuite}
            onCharger={liste.chargerLaSuite}
          />
        </Carte>
      ) : null}
    </div>
  );
}
